/**
 * Pure helpers for the Insights area: the period and the one it's compared
 * with, where each count drills down to, and how a number, a rate or a
 * change reads. Kept free of React so the date maths and the "is this
 * sample big enough to compare" rules are unit-tested.
 *
 * Periods are whole UTC days, half-open [from, to), ending with today —
 * the same days the Invites page means by its `YYYY-MM-DD` filters (see
 * lib/teamPipeline), so a count and the list it opens cover the same rows.
 */
import type { Money } from '@/api/insights';
import { invitesListHref } from './chart';
import { formatMoney } from './money';
import type { InviteStageFilter } from './moneyPages';
import { formatDayRange, pipelineLinkDates, type DayRange } from './teamPipeline';
import { invitesBaseForTeam, teamSection } from './teams';

export type { DayRange } from './teamPipeline';
export { formatDayRange } from './teamPipeline';

const DAY = 24 * 60 * 60 * 1000;

// ── Periods ───────────────────────────────────────────────────────────────

export type InsightsPreset = '7d' | '30d' | '90d' | '365d' | 'custom';

export const INSIGHTS_PRESETS: readonly { key: Exclude<InsightsPreset, 'custom'>; label: string; days: number }[] = [
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
  { key: '90d', label: '90 days', days: 90 },
  { key: '365d', label: '12 months', days: 365 },
];

export const DEFAULT_INSIGHTS_PRESET: Exclude<InsightsPreset, 'custom'> = '30d';

/** `?period=` from the URL. Unknown values (old links' `all`, `month`) fall back to the default. */
export function parseInsightsPreset(raw: string | null | undefined): InsightsPreset {
  if (raw === 'custom') return 'custom';
  return INSIGHTS_PRESETS.some((p) => p.key === raw) ? (raw as InsightsPreset) : DEFAULT_INSIGHTS_PRESET;
}

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD` → that UTC midnight, or null when malformed (2026-02-31 included). */
export function parseUtcDay(day: string): Date | null {
  const m = DAY_RE.exec(day);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return date;
}

export interface InsightsWindows {
  current: DayRange;
  previous: DayRange;
  /** Days in each window. */
  days: number;
  /** Set when a custom range couldn't be used as typed (the default period is shown instead). */
  problem: string | null;
}

/**
 * The period and the same-length period directly before it. Presets are
 * today and the N−1 days before. A custom range covers its first through
 * its last day; one typed backwards is swapped, and an incomplete or
 * invalid one falls back to the default preset with a `problem` to show.
 */
export function insightsWindows(
  preset: InsightsPreset,
  custom: { from: string; to: string } = { from: '', to: '' },
  now: Date = new Date(),
): InsightsWindows {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const build = (start: number, end: number, problem: string | null = null): InsightsWindows => {
    const days = Math.round((end - start) / DAY);
    return {
      current: { from: new Date(start), to: new Date(end) },
      previous: { from: new Date(start - days * DAY), to: new Date(start) },
      days,
      problem,
    };
  };
  const fromPreset = (key: Exclude<InsightsPreset, 'custom'>, problem: string | null = null) => {
    const days = INSIGHTS_PRESETS.find((p) => p.key === key)!.days;
    return build(today + DAY - days * DAY, today + DAY, problem);
  };
  if (preset !== 'custom') return fromPreset(preset);

  let from = custom.from ? parseUtcDay(custom.from) : null;
  let to = custom.to ? parseUtcDay(custom.to) : null;
  if ((custom.from && !from) || (custom.to && !to)) {
    return fromPreset(DEFAULT_INSIGHTS_PRESET, 'That date isn’t valid, so the last 30 days are shown.');
  }
  if (!from || !to) return fromPreset(DEFAULT_INSIGHTS_PRESET, 'Pick a start and an end date. Showing the last 30 days until then.');
  if (from > to) [from, to] = [to, from];
  return build(from.getTime(), to.getTime() + DAY);
}

/** The window as API params (ISO, half-open). */
export function windowParams(r: DayRange): { dateFrom: string; dateTo: string } {
  return { dateFrom: r.from.toISOString(), dateTo: r.to.toISOString() };
}

/** "1 Sep – 30 Sep 2026" for a window the API sent back (ISO, half-open). */
export function formatWindow(w: { from: string; to: string } | null | undefined): string | null {
  if (!w) return null;
  const from = new Date(w.from);
  const to = new Date(w.to);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return formatDayRange({ from, to });
}

/** Joins are dated (verified_at) from this day on; activity-mode joins before it read 0. */
export const JOINS_DATED_FROM = '2026-10-01';

/** True when the window starts before joins were dated, so "joined in this period" undercounts. */
export function windowPredatesJoinDates(r: DayRange): boolean {
  return r.from.getTime() < parseUtcDay(JOINS_DATED_FROM)!.getTime();
}

// ── Old links ─────────────────────────────────────────────────────────────

export type InsightsTabKey = 'scorecard' | 'funnel' | 'outreachers' | 'spend' | 'sources' | 'quality';

/** Funnel & ROI's `?view=` → the tab that replaced it. */
const LEGACY_VIEW_TABS: Record<string, InsightsTabKey> = {
  funnel: 'funnel',
  roi: 'spend',
  attribution: 'sources',
  attention: 'scorecard',
};

/**
 * Where an old reporting page's link lands: Performance → Outreachers, Niche
 * performance → Sources, Funnel & ROI's views → Funnel / Spend / Sources
 * (Needs attention → Scorecard). Period and team carry over when they still
 * mean something; everything else is dropped.
 */
export function legacyInsightsTarget(page: 'inviters' | 'niches' | 'efficiency', search: string): string {
  const old = new URLSearchParams(search);
  const next = new URLSearchParams();
  const tab: InsightsTabKey =
    page === 'inviters' ? 'outreachers' : page === 'niches' ? 'sources' : (LEGACY_VIEW_TABS[old.get('view') ?? ''] ?? 'funnel');
  next.set('tab', tab);
  const period = old.get('period');
  if (period && (period === 'custom' || INSIGHTS_PRESETS.some((p) => p.key === period))) next.set('period', period);
  for (const key of ['from', 'to', 'team', 'channel']) {
    const v = old.get(key);
    if (v) next.set(key, v);
  }
  return `/admin/insights?${next.toString()}`;
}

// ── Drill-downs ───────────────────────────────────────────────────────────

/** Valid invites: every stage but Not paid (and hidden invites never show). */
export const REPLIED_STAGES: readonly InviteStageFilter[] = ['replied', 'expired', 'joined', 'posted'];
export const JOINED_STAGES: readonly InviteStageFilter[] = ['joined', 'posted'];
export const POSTED_STAGES: readonly InviteStageFilter[] = ['posted'];

export type DrillDate = 'logged' | 'joined' | 'posted';

export interface DrillOptions {
  /** The team the count belongs to (a team row), else the page's scope. */
  team?: string;
  stages?: readonly InviteStageFilter[];
  /** Which date the window filters on: when logged (cohort), joined or posted (activity). */
  by?: DrillDate;
  window?: DayRange;
  inviterId?: number;
  channel?: string;
  niche?: string;
  /** Discovery folder name (the Invites page's Source filter). */
  source?: string;
  owed?: boolean;
}

/**
 * Link from a count to the invites behind it. A team with its own section
 * (Influencer, Vyral) opens there; any other team rides along as `?team=`;
 * with no team the main Invites list has the same default scope the main
 * Insights does.
 */
export function insightsInvitesHref(opts: DrillOptions): string {
  const team = opts.team ?? '';
  const base = invitesBaseForTeam(team);
  const scopedTeam = team && !teamSection(team) ? team : null;
  const days = opts.window ? pipelineLinkDates(opts.window) : null;
  const by = opts.by ?? 'logged';
  return invitesListHref({
    base,
    team: scopedTeam,
    stages: opts.stages,
    inviterId: opts.inviterId ?? null,
    channel: opts.channel ?? null,
    niche: opts.niche ?? null,
    source: opts.source ?? null,
    owed: opts.owed,
    ...(days && by === 'logged' ? { dateFrom: days.dateFrom, dateTo: days.dateTo } : {}),
    ...(days && by === 'posted' ? { convertedFrom: days.dateFrom, convertedTo: days.dateTo } : {}),
    ...(days && by === 'joined' ? { verifiedFrom: days.dateFrom, verifiedTo: days.dateTo } : {}),
  });
}

// ── Numbers ───────────────────────────────────────────────────────────────

/** Below this many in the denominator a rate is shown greyed out: too few to compare. */
export const MIN_SAMPLE = 10;

export function isThinSample(n: number | null | undefined): boolean {
  return n == null || n < MIN_SAMPLE;
}

/** A money value (string or number) as a number, or null when absent / not a number. */
export function toNum(v: Money | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number.parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** "$1,234.50", or "—" for null. */
export function money(v: Money | null | undefined): string {
  const n = toNum(v);
  return n === null ? '—' : `$${formatMoney(n)}`;
}

/** "$1.2k" style for chart labels where space is tight. */
export function compactMoney(v: Money | null | undefined): string {
  const n = toNum(v);
  if (n === null) return '—';
  if (Math.abs(n) >= 10_000) return `$${Math.round(n / 1000)}k`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

/** "34%" / "3.4%": whole percent from 10% up, one decimal below. "—" for null. */
export function pctText(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) return '—';
  const p = rate * 100;
  return p >= 10 || p === 0 ? `${Math.round(p)}%` : `${p.toFixed(1)}%`;
}

/** Days as "2.5 d", "—" for null. */
export function daysText(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return v >= 10 ? `${Math.round(v)} d` : `${v.toFixed(1)} d`;
}

// ── Changes ───────────────────────────────────────────────────────────────

export type ChangeTone = 'good' | 'bad' | 'flat' | 'none';

export interface Change {
  /** "+18%", "−3", "+2.1 pts", "new" … */
  text: string;
  tone: ChangeTone;
}

/**
 * A value's change on the previous period. Counts and money read as a
 * percentage once the previous period had anything; from zero they read as
 * the raw difference. `lowerIsBetter` flips the colour (cost per post).
 * Null when either side is missing.
 */
export function changeOf(
  current: number | null,
  previous: number | null,
  opts: { lowerIsBetter?: boolean; kind?: 'count' | 'money' | 'rate' } = {},
): Change | null {
  if (current === null || previous === null) return null;
  const kind = opts.kind ?? 'count';
  const diff = current - previous;
  const toneFor = (d: number): ChangeTone => {
    if (d === 0) return 'flat';
    return (d < 0) === !!opts.lowerIsBetter ? 'good' : 'bad';
  };
  if (kind === 'rate') {
    const pts = Math.round(diff * 1000) / 10;
    if (pts === 0) return { text: 'no change', tone: 'flat' };
    return { text: `${pts > 0 ? '+' : '−'}${Math.abs(pts).toFixed(1)} pts`, tone: toneFor(pts) };
  }
  if (diff === 0) return { text: 'no change', tone: 'flat' };
  if (previous === 0) {
    const abs = kind === 'money' ? compactMoney(Math.abs(diff)) : Math.abs(diff).toLocaleString();
    return { text: `${diff > 0 ? '+' : '−'}${abs}`, tone: toneFor(diff) };
  }
  const p = Math.round((diff / Math.abs(previous)) * 100);
  if (p === 0) return { text: '±0%', tone: 'flat' };
  return { text: `${p > 0 ? '+' : '−'}${Math.abs(p)}%`, tone: toneFor(p) };
}

/** Share of a total, null when the total is 0. */
export function shareOf(part: number, total: number): number | null {
  return total > 0 ? part / total : null;
}
